import { create } from 'zustand';
import {
  requestMagicCode,
  verifyMagicCode,
  logoutApi,
  logoutAllApi,
  MagicCodeRequestDto,
  MagicCodeVerifyDto,
} from '../api/authApi';
import {
  BaseState,
  createLoadingWrapper,
} from '../../../shared/stores/storeHelpers';
import {
  extractUserInfo,
  parseToken,
  getTokenIdentifier,
  getErrorMessage,
  isTokenExpired,
  TokenRefreshAuthError,
} from '@billda/shared-ui';
import { useMultiAccountStore } from './multiAccountStore';
import {
  clearAllCaches,
  invalidateAllQueries,
} from '../../../shared/lib/queryClient';

/**
 * Lazy reference to the shared token refresh function from axiosClient.
 * Uses dynamic import to avoid circular dependency at import time.
 * The module system caches the import, so no manual caching is needed.
 */
async function getSharedTokenRefresh(): Promise<
  (() => Promise<string>) | null
> {
  try {
    const { sharedTokenRefresh } = await import(
      '../../../shared/api/axiosClient'
    );
    return sharedTokenRefresh;
  } catch {
    return null;
  }
}

// Auth result interface for magic code operations
interface AuthResult {
  success: boolean;
  error?: string;
  shouldShowError?: boolean;
}

// Separate interfaces for clear state/actions separation
interface AuthState extends BaseState {
  isAuthenticated: boolean;
  token: string | null; // Identity token - also synced to sessionStorage
  // Note: refreshToken is stored in multiAccountStore (persistent, multi-account)
}

interface AuthActions {
  clearError: () => void;
  setTokens: (tokens: { token: string; refreshToken: string }) => void;
  logout: (reason?: string) => Promise<void>;
  logoutAll: (reason?: string) => Promise<void>;
  switchAccount: (email: string) => Promise<void>;

  // Magic code auth actions
  requestMagicCode: (email: string) => Promise<AuthResult>;
  verifyMagicCode: (email: string, code: string) => Promise<AuthResult>;

  // Refresh token action
  refreshAccessToken: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState & AuthActions>()((set, get) => {
  // Private helper functions using strongly-typed utilities
  const { withLoadingReturn } = createLoadingWrapper<AuthState>();

  const resetAuthState = () => ({
    isAuthenticated: false,
    token: null,
    error: '',
  });

  return {
    // State
    isAuthenticated: false,
    token: null,
    loading: false,
    error: '',

    clearError: () => set({ error: '' }),

    setTokens: (tokens: { token: string; refreshToken: string }) => {
      console.log('[TabSync] setTokens called', {
        tokenTail: tokens.refreshToken?.slice(-8),
      });

      // Extract user info from identity token
      const userInfo = extractUserInfo(tokens.token);

      if (!userInfo) {
        console.error('Failed to extract user info from token');
        set({ error: 'Invalid token format', isAuthenticated: false });
        return;
      }

      // Extract organisation ID from token for cross-tab sync
      const parsedToken = parseToken(tokens.token);

      console.log('[TabSync] setTokens writing to multiAccountStore', {
        email: userInfo.email,
        orgId: parsedToken?.currentOrganisationId,
        tokenTail: tokens.refreshToken?.slice(-8),
      });

      // Store account with refresh token in multiAccountStore (persistent)
      useMultiAccountStore.getState().addAccount({
        email: userInfo.email,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        refreshToken: tokens.refreshToken,
        currentOrganisationId: parsedToken?.currentOrganisationId,
      });

      // Set this as the current account
      useMultiAccountStore.getState().setCurrentAccount(userInfo.email);

      console.log('[TabSync] setTokens complete', {
        storedTail: useMultiAccountStore
          .getState()
          .getCurrentAccount()
          ?.refreshToken?.slice(-8),
        lastUpdatedAt: useMultiAccountStore.getState().lastUpdatedAt,
      });

      // Update auth state with token
      set({
        isAuthenticated: true,
        token: tokens.token,
        error: '',
      });
    },

    logout: async (reason = 'unknown') => {
      const currentState = get();
      const tokenIdentifier = currentState.token
        ? getTokenIdentifier(currentState.token)
        : null;

      console.log('🚪 Portal logout:', {
        reason,
        tokenIdentifier,
        wasAuthenticated: currentState.isAuthenticated,
        currentRoute:
          typeof window !== 'undefined' ? window.location.pathname : 'unknown',
        timestamp: new Date().toISOString(),
      });

      // Fire-and-forget: revoke refresh token on server.
      // The endpoint is unauthenticated so no identity token or refresh needed.
      const currentAccount = useMultiAccountStore
        .getState()
        .getCurrentAccount();
      if (currentAccount?.refreshToken) {
        logoutApi(currentAccount.refreshToken);
      }

      // Clear React Query cache first
      clearAllCaches();

      // Clear all stored accounts from localStorage
      console.log('🗑️ Clearing all accounts from localStorage');
      useMultiAccountStore.getState().clearAllAccounts();
      console.log(
        '🗑️ Accounts after clear:',
        useMultiAccountStore.getState().accounts
      );

      // Reset auth state
      set(resetAuthState());
    },

    logoutAll: async (reason = 'unknown') => {
      const currentState = get();
      const tokenIdentifier = currentState.token
        ? getTokenIdentifier(currentState.token)
        : null;

      console.log('🚪 Portal logout all devices:', {
        reason,
        tokenIdentifier,
        wasAuthenticated: currentState.isAuthenticated,
        currentRoute:
          typeof window !== 'undefined' ? window.location.pathname : 'unknown',
        timestamp: new Date().toISOString(),
      });

      // Fire-and-forget: revoke all refresh tokens on server.
      // The endpoint is unauthenticated so no identity token or refresh needed.
      const currentAccount = useMultiAccountStore
        .getState()
        .getCurrentAccount();
      if (currentAccount?.refreshToken) {
        logoutAllApi(currentAccount.refreshToken);
      }

      // Clear React Query cache first
      clearAllCaches();

      // Clear all stored accounts from localStorage
      console.log('🗑️ Clearing all accounts from localStorage (logout all)');
      useMultiAccountStore.getState().clearAllAccounts();

      // Reset auth state
      set(resetAuthState());
    },

    switchAccount: async (email: string) => {
      // Get the account from the store
      const account = useMultiAccountStore.getState().getAccount(email);

      if (!account) {
        console.error(`Cannot switch to account: ${email} not found`);
        throw new Error('Account not found');
      }

      // Check if refresh token is expired
      const expired = isTokenExpired(account.refreshToken);
      if (expired === true) {
        throw new Error('EXPIRED_REFRESH_TOKEN');
      }

      // Set the new account as current
      useMultiAccountStore.getState().setCurrentAccount(email);

      // Immediately refresh the identity token for the new account
      // This will set isAuthenticated, token, and invalidate queries
      const refreshed = await get().refreshAccessToken();

      if (!refreshed) {
        throw new Error('Failed to refresh token for new account');
      }
    },

    // Magic code login actions
    requestMagicCode: async (email: string): Promise<AuthResult> => {
      const result = await withLoadingReturn(
        set,
        async () => {
          const magicCodeRequest: MagicCodeRequestDto = { email };
          await requestMagicCode(magicCodeRequest);

          set({ error: '' });
          return { success: true };
        },
        {
          customErrorHandler: error => {
            console.error('Error during magic code request:', error);
            return {
              success: false,
              error: getErrorMessage(error),
              shouldShowError: true,
            };
          },
        }
      );

      return (
        result ?? {
          success: false,
          error: 'Connection error. Please check your network and try again.',
          shouldShowError: true,
        }
      );
    },

    verifyMagicCode: async (
      email: string,
      code: string
    ): Promise<AuthResult> => {
      const result = await withLoadingReturn(
        set,
        async () => {
          const verifyRequest: MagicCodeVerifyDto = { email, code };
          const response = await verifyMagicCode(verifyRequest);

          const token = response.identityToken;
          const refreshToken = response.refreshToken;
          if (token && refreshToken) {
            // Use setTokens to properly store tokens
            get().setTokens({ token, refreshToken });

            // Clear any existing cache from previous sessions/accounts
            clearAllCaches();

            return { success: true };
          } else {
            console.error('No token in response:', response);
            return {
              success: false,
              error: 'Authentication failed. Please try again.',
              shouldShowError: true,
            };
          }
        },
        {
          customErrorHandler: error => {
            console.error('Error during magic code verification:', error);
            return {
              success: false,
              error: getErrorMessage(error),
              shouldShowError: true,
            };
          },
        }
      );

      return (
        result ?? {
          success: false,
          error: 'Connection error. Please check your network and try again.',
          shouldShowError: true,
        }
      );
    },

    refreshAccessToken: async (): Promise<boolean> => {
      const currentAccount = useMultiAccountStore
        .getState()
        .getCurrentAccount();
      const currentRefreshToken = currentAccount?.refreshToken || null;

      console.log('[TabSync] refreshAccessToken called', {
        hasCurrentAccount: !!currentAccount,
        currentEmail: currentAccount?.email,
        tokenTail: currentRefreshToken?.slice(-8),
        lastUpdatedAt: useMultiAccountStore.getState().lastUpdatedAt,
      });

      if (!currentRefreshToken) {
        console.log(
          '[TabSync] refreshAccessToken: no refresh token, returning false'
        );
        return false;
      }

      try {
        // Use the shared token refresh function (with deduplication)
        // to avoid race conditions with the interceptor's refresh path.
        // The shared function calls authStore.setTokens() via the adapter,
        // which updates both authStore state and multiAccountStore.
        const sharedRefresh = await getSharedTokenRefresh();

        if (sharedRefresh) {
          await sharedRefresh();
          // Invalidate queries so they refetch with the new token
          // (the shared refresh doesn't do this - it processes its own queue)
          invalidateAllQueries();
          return true;
        }

        // Fallback: should not happen in production, but handles test scenarios
        // where interceptors are disabled
        console.warn(
          'Shared token refresh not available, this may cause race conditions'
        );
        return false;
      } catch (error) {
        if (error instanceof TokenRefreshAuthError) {
          // Auth failure - the shared handler already called onAuthFailure
          // which triggers logout, so we don't need to logout again
          return false;
        }
        // Network errors - the shared handler shows retry UI,
        // user stays logged in
        return false;
      }
    },
  };
});
